import React, {useEffect, useState} from 'react';
import axios from 'axios';

/**
 * Component for loading images with proper authorization
 * This handles the 403 Forbidden error by fetching images with auth headers
 */
const AuthImage = ({src, alt, className, style}) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        // Skip processing for data URLs or blob URLs
        if (src && (src.startsWith('data:') || src.startsWith('blob:'))) {
            setImageSrc(src);
            setLoading(false);
            return;
        }

        // Skip if source is not a backend URL
        if (!src || (!src.includes('/attach/') && !src.includes('localhost'))) {
            setImageSrc(src);
            setLoading(false);
            return;
        }

        const loadImageWithAuth = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    setImageSrc(src); // Fallback to original source
                    setLoading(false);
                    return;
                }

                const response = await axios.get(src, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    responseType: 'blob'
                });

                // Create a blob URL from the response
                const blobUrl = URL.createObjectURL(response.data);
                setImageSrc(blobUrl);
                setLoading(false);
            } catch (err) {
                console.error('Failed to load image with auth:', err);
                setError(true);
                setLoading(false);
                // Set a placeholder or fallback image
                setImageSrc('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"%3E%3Crect x="3" y="3" width="18" height="18" rx="2" ry="2"%3E%3C/rect%3E%3Ccircle cx="8.5" cy="8.5" r="1.5"%3E%3C/circle%3E%3Cpolyline points="21 15 16 10 5 21"%3E%3C/polyline%3E%3C/svg%3E');
            }
        };

        loadImageWithAuth();

        // Cleanup function to revoke blob URLs
        return () => {
            if (imageSrc && imageSrc.startsWith('blob:')) {
                URL.revokeObjectURL(imageSrc);
            }
        };
    }, [src]);

    if (loading) {
        return (
            <div
                className={className}
                style={{
                    ...style,
                    backgroundColor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#999" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                </svg>
            </div>
        );
    }

    if (error) {
        return (
            <div
                className={className}
                style={{
                    ...style,
                    backgroundColor: '#f8d7da',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
        );
    }

    return (
        <img
            src={imageSrc}
            alt={alt}
            className={className}
            style={style}
            onError={() => setError(true)}
        />
    );
};

export default AuthImage;